import { Fragment, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import FloatingBulb from "@/components/FloatingBulb";
import FractionalBanner from "@/components/FractionalBanner";
import { Reveal } from "@/components/motion";
import { fetchPosts, formatDate } from "@/lib/blog";
import { FAQS, FAQ_CATEGORIES, faqPageJsonLd } from "@/lib/faq";
import { authorForBlog, authorForFaq } from "@/lib/authors";
import { useSeo } from "@/lib/seo";

const BLOG_TO_TOPIC = { Finance: "Finanza e controllo", HR: "Persone e lavoro", Marketing: "Marketing", Organizzazione: "Organizzazione", Legale: "Legale" };
const TOPICS = [...FAQ_CATEGORIES.filter((c) => c !== "Metodo"), "Organizzazione", "Legale", "Metodo"];
const slugifyTopic = (c) => c.toLowerCase().replace(/\s+/g, "-");

const faqItem = (f) => ({ id: `faq-${f.slug}`, slug: f.slug, topic: f.category, title: f.q, excerpt: f.a, href: `/esperto-risponde/${f.slug}`, author: authorForFaq(f), date: null });
const postItem = (p) => ({ id: p.id, slug: p.slug, topic: BLOG_TO_TOPIC[p.category] || p.category, title: p.title, excerpt: p.excerpt, href: `/blog/${p.slug}`, author: authorForBlog(p), date: p.published_at });

export default function Blog() {
  const [params, setParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const topicParam = params.get("tab");
  const active = TOPICS.find((t) => slugifyTopic(t) === topicParam) || "Tutte";

  useSeo({
    title: "L'esperto risponde: domande e risposte per imprese e startup | Idea 360",
    description:
      "Le domande che ci fanno gli imprenditori e le risposte dei nostri referenti: contributi a fondo perduto, business plan, controllo di gestione, assunzioni, marketing, organizzazione. Idea 360 Consulting, Campania.",
    path: "/blog",
    jsonLd: faqPageJsonLd(FAQS),
  });

  useEffect(() => {
    fetchPosts().then(setPosts).catch(() => {});
  }, []);

  const items = useMemo(() => [...posts.map(postItem), ...FAQS.map(faqItem)], [posts]);
  const visible = active === "Tutte" ? items : items.filter((i) => i.topic === active);
  const topics = TOPICS.filter((t) => items.some((i) => i.topic === t));

  return (
    <>
      <section className="relative">
        <FloatingBulb size={32} className="absolute top-24 right-[10%] hidden md:block" testId="esperto-bulb" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-10 pb-10 sm:pt-28 sm:pb-12">
          <Reveal as="p" y={14} className="eyebrow mb-5">L'esperto risponde</Reveal>
          <Reveal
            as="h1"
            delay={0.1}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white max-w-4xl leading-[1.05]"
            style={{ fontFamily: "'Outfit', sans-serif", letterSpacing: "-0.03em" }}
            data-testid="esperto-h1"
          >
            Le domande che ci fanno <span className="text-[#FACC15]">gli imprenditori</span>.
          </Reveal>
          <Reveal as="p" delay={0.2} className="mt-6 text-white/70 text-lg max-w-3xl leading-relaxed">
            Contributi a fondo perduto, business plan, controllo di gestione, assunzioni,
            marketing, avvio di una nuova attività: qui trovi risposte dirette e approfondimenti,
            scritti dai professionisti che seguono ogni giorno imprese in Campania e in tutta Italia.
          </Reveal>
        </div>
      </section>

      <section className="pb-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-wrap gap-2">
          {["Tutte", ...topics].map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setParams(c === "Tutte" ? {} : { tab: slugifyTopic(c) })}
              data-testid={`faq-filter-${slugifyTopic(c)}`}
              className={`text-xs tracking-wide px-4 py-3 min-h-[44px] inline-flex items-center border transition-colors ${
                active === c ? "border-[#FACC15] text-[#FACC15] bg-[#FACC15]/10" : "border-[#262626] text-white/70 hover:border-[#FACC15]/50"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      <section className="pb-16 sm:pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="esperto-list">
          {visible.map((it, i) => (
            <Fragment key={it.id}>
              {i === 6 && <FractionalBanner className="lg:col-span-2 my-4" />}
              <Reveal delay={(i % 2) * 0.08}>
              <article className="lift border border-[#1a1a1a] bg-[#0c0c0c] p-7 flex flex-col h-full" data-testid={`esperto-card-${it.slug}`}>
                <div className="flex items-center justify-between gap-3 mb-4">
                  <p className="text-[#FACC15] text-xs tracking-[0.18em] uppercase">{it.topic}</p>
                  {it.date && <time className="text-white/40 text-xs" dateTime={it.date}>{formatDate(it.date)}</time>}
                </div>
                <h2 className="text-xl text-white font-semibold mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {it.title}
                </h2>
                <p className="text-white/70 text-sm leading-relaxed flex-1">{it.excerpt}</p>
                <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <p className="text-white/45 text-xs" data-testid="esperto-card-author">Risponde {it.author.name}</p>
                  <Link to={it.href} className="tap-target gap-2 text-sm text-[#FACC15] hover:gap-3 transition-all shrink-0" data-testid={`esperto-link-${it.slug}`}>
                    Leggi la risposta completa
                    <ArrowRight size={15} />
                  </Link>
                </div>
              </article>
            </Reveal>
            </Fragment>
          ))}
          {visible.length > 0 && visible.length <= 6 && <FractionalBanner className="lg:col-span-2 my-4" />}
          {visible.length === 0 && <p className="text-white/55" data-testid="esperto-empty">Nessun contenuto in questa categoria, per ora.</p>}
        </div>
      </section>

      <section className="py-14 sm:py-28 border-t border-[#141414]">
        <Reveal className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6" style={{ fontFamily: "'Outfit', sans-serif", letterSpacing: "-0.025em" }}>
            Hai una domanda che non trovi qui?
          </h2>
          <p className="text-white/65 text-base mb-8">
            Scrivicela: rispondiamo entro 24 ore e, se è utile ad altri imprenditori, la pubblichiamo in questa sezione.
          </p>
          <Link to="/contatti" className="btn-yellow inline-flex items-center gap-2" data-testid="esperto-cta">
            Fai la tua domanda
            <ArrowRight size={18} />
          </Link>
        </Reveal>
      </section>
    </>
  );
}
